/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { storeToRefs } from 'pinia'
import type { RefreshTokenParams, TokenInfo } from '../types/cognito/auth'
import {
  getLastAccessDate,
  getSessionInfo,
  tempDataGetItem,
  tempDataSetItem,
  webApiCall
} from '../utils/miiaFwUtils'
import { watch } from 'vue'
import type { ErrorMessage } from '../types/error'
import { useMiiaFwStoreUtils } from './useMiiaFwStoreUtils'
import MiiaFwConfig from '@/configs/config'
import {
  getAuthApiSubdir,
  getUserIdToStorage,
  procAtSignout,
  setUserIdToStorage,
  signin,
  updateCredentials,
  updateIsAuthorized
} from '../utils/miiaFwCognito'
import { useLoadingView } from './useLoadingView'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { useRouter } from 'vue-router'
import { useRecertificationDialogStore } from '@/stores/recertificationDialogStore'

export const useToken = () => {
  const router = useRouter()
  const { setLoadingView } = useLoadingView()
  const { updateRefreshState } = useMiiaFwStoreUtils()
  const commonModalStore = useCommonModalStore()
  /**
   * トークンをリフレッシュする。
   * @returns If resolved then {'access_token': (string)アクセストークン, 'refresh_token': (string)リフレッシュトークン, 'id_token': (string)IDトークン, 'device_key': (string)デバイスキー, 'auth_token': Authトークン, 'token_secret': (string)トークンシークレット, 'provider': (string)IDプロバイダ}. セッション情報を返す。
   * If rejected then {'message': '[エラーメッセージ]'}.
   */
  const refreshToken = async (): Promise<TokenInfo | string | null | ErrorMessage> => {
    const miiaFwStore = useMiiaFwStore()
    const { refreshState } = storeToRefs(miiaFwStore)
    // console.log('リフレッシュ要求実行')
    const tokens = getTokens()
    return new Promise((resolve, reject) => {
      if (tokens === null) {
        reject({ message: 'サインアウト中です。' })
      } else {
        // refreshState.onRefreshがtrue -> falseに変わる(リフレッシュ処理が完了する)のを待つ
        if (refreshState.value?.onRefresh) {
          const unwatch = watch(
            refreshState,
            newVal => {
              if (newVal?.onRefresh === false) {
                // リフレッシュが完了した場合
                if (newVal.error) {
                  reject({ message: newVal.error })
                } else {
                  // console.log('リフレッシュ処理回避')
                  const refreshedTokens = getTokens()
                  resolve(refreshedTokens)
                }
              }
              unwatch() // 監視を解除
            },
            { deep: true }
          )
          // タイムアウト処理はあえて実装しない。(Tokenリフレッシュされずに放置が多重で発生するリスクが極めて低いため)
        } else {
          updateRefreshState(true) // リフレッシュロックフラグを有効化(refreshTokenProc()の処理内で無効化)
          if (
            Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
            tokens.provider.toLowerCase() === 'cognito'
          ) {
            const { ttl } = MiiaFwConfig.config.authorization
            if (typeof ttl !== 'number') {
              throw new Error('[refreshToken/config] ttl を設定する必要があります')
            }
            const now = Date.now()
            // console.log('現在の日時(エポックミリ秒): ' + now)
            // console.log('前回APIコール日時: ' + getLastAccessDate())
            // console.log('前回APIコール日時からの経過時間[ms]: ' + (now - getLastAccessDate()))
            // console.log('TTL: ' + MiiaFwConfig.config.authorization.ttl)
            // console.log('リフレッシュ処理開始')
            const refreshTokenResult =
              now - getLastAccessDate() < ttl
                ? refreshTokenProc(tokens)
                    .then(result => {
                      resolve(result)
                      return true
                    })
                    .catch(error => {
                      console.warn('トークンリフレッシュに失敗', error.message)
                      if (error.message.startsWith('Error: データ受信に失敗しました。')) {
                        reject(error)
                      }
                      return false
                    })
                : false
            if (refreshTokenResult) {
              return
            }

            // console.log('パスワードダイアログ表示')
            recertification(tokens)
              .then(result => {
                resolve(result)
              })
              .catch(error => {
                reject({ message: error.message })
              })
          } else {
            updateRefreshState(false, '対応していないIDプロバイダが設定されました。')
            reject({ message: '対応していないIDプロバイダが設定されました。' })
          }
        }
      }
    })
  }
  /**
   * セッションストレージからセッション情報を取得する。
   * @returns {'access_token': (string)アクセストークン, 'refresh_token': (string)リフレッシュトークン, 'id_token': (string)IDトークン, 'device_key': (string)デバイスキー, 'auth_token': (string)Authトークン, 'token_secret': (string)トークンシークレット, 'provider': (string)IDプロバイダ}
   */
  const getTokens = (): TokenInfo | null => {
    const ret = {} as any
    let accessToken = getSessionInfo('access_token')
    if (accessToken.length === 0) {
      accessToken = tempDataGetItem('access_token')
    }
    if (accessToken) ret.access_token = accessToken
    const refreshToken = tempDataGetItem('refresh_token')
    if (refreshToken) ret.refresh_token = refreshToken
    let idToken = getSessionInfo('id_token')
    if (idToken.length === 0) {
      idToken = tempDataGetItem('id_token')
    }
    if (idToken) ret.id_token = idToken
    let deviceKey = getSessionInfo('device_key')
    if (deviceKey.length === 0) {
      deviceKey = tempDataGetItem('device_key')
    }
    if (deviceKey) ret.device_key = deviceKey
    const authToken = tempDataGetItem('auth_token')
    if (authToken) ret.auth_token = authToken
    const tokenSecret = tempDataGetItem('token_secret')
    if (tokenSecret) ret.token_secret = tokenSecret
    const sessionId = tempDataGetItem('session_id')
    if (sessionId) ret.session_id = sessionId
    const provider = tempDataGetItem('provider')
    if (provider) ret.provider = provider
    if (Object.keys(ret).length === 0) {
      return null
    } else {
      return ret
    }
  }

  /**
   * トークンのでのリフレッシュ処理の本体(cognito認証専用)。
   * @param {Object} tokens {refresh_token: '(string)リフレッシュトークン'}
   * @returns {Promise<object>} If resolved then {'access_token': (string)アクセストークン, 'refresh_token': (string)リフレッシュトークン, 'id_token': (string)IDトークン, 'device_key': (string)デバイスキー, 'auth_token': Authトークン, 'token_secret': (string)トークンシークレット, 'provider': (string)IDプロバイダ}. セッション情報を返す。
   * If rejected then {'message': '[エラーメッセージ]'}.
   */
  const refreshTokenProc = async (tokens: TokenInfo): Promise<TokenInfo | ErrorMessage> => {
    return new Promise((resolve, reject) => {
      const rtParams: RefreshTokenParams = {
        refresh_token: tokens.refresh_token
      }
      if (MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'iam') {
        rtParams.access_token = tokens.access_token
      }
      const args = { function: 'refresh_token', params: rtParams }
      if (
        Object.prototype.hasOwnProperty.call(tokens, 'device_key') &&
        typeof tokens.device_key === 'string' &&
        tokens.device_key.length > 0
      ) {
        args.params.device_key = tokens.device_key
      }
      const url: string = MiiaFwConfig.config.authEndpoint.concat('/', getAuthApiSubdir())
      webApiCall(
        url,
        'POST',
        MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'custom_authorizer'
          ? { 'X-miiafw-token': tokens.access_token }
          : {},
        args,
        'json'
      )
        .then(data => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (Object.prototype.hasOwnProperty.call(data, 'error')) {
            // ハンドリングされたエラー処理
            updateRefreshState(false, data.error)
            reject({ message: data.error })
          } else {
            if (data.result.isUpdated) {
              setTokens(data.result.tokens)
              setUserIdToStorage(data.result.userId)
              updateIsAuthorized(true)
              updateCredentials(data.result.tokens) // 【後で削除】S3でAWS SDKを使用するために必要
            }
            updateRefreshState(false)
            resolve(data.result.tokens)
          }
        })
        .catch(error => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          updateRefreshState(false, error.message)
          reject({ message: error.message })
        })
    })
  }

  /**
   * セッションストレージへセッション情報を格納する。
   * @param tokens セッション情報
   */
  const setTokens = (tokens: Partial<TokenInfo> & Pick<TokenInfo, 'provider'>): void => {
    // Cookieから取得 → そのまま、Bodyから取得 → セッションストレージに格納する。
    if (getSessionInfo('access_token').length < 100 || getSessionInfo('id_token').length < 100) {
      tempDataSetItem('access_token', tokens.access_token ? tokens.access_token : '')
      tempDataSetItem('refresh_token', tokens.refresh_token ? tokens.refresh_token : '')
      tempDataSetItem('id_token', tokens.id_token ? tokens.id_token : '')
      tempDataSetItem('device_key', tokens.device_key ? tokens.device_key : '')
      tempDataSetItem('auth_token', tokens.auth_token ? tokens.auth_token : '')
      tempDataSetItem('token_secret', tokens.token_secret ? tokens.token_secret : '')
      tempDataSetItem('session_id', tokens.session_id ? tokens.session_id : '')
      tempDataSetItem('provider', tokens.provider)
    } else {
      // Cookieのサイズ制限があるのでリフレッシュトークンはBodyで取得してセッション
      tempDataSetItem('refresh_token', tokens.refresh_token ? tokens.refresh_token : '')
      tempDataSetItem('provider', tokens.provider)
    }
  }

  /**
   * invokeApiGwFuncが呼び出す、認証されているときに実行を許可する処理の雛形。
   * トークンをセッションスレージから取得出来た場合は認証の必要なAPIを実行し、取得できなかった場合にはアラートメッセージを出力してトップページに遷移させる。
   * @param callback 認証が必要な動作を行うコールバック関数
   * @param loadingView ローディング画面を表示するかどうか
   */
  const procUnderAuthed = (callback: Function, loadingView: boolean = true): void => {
    const invokeCallback = (): void => {
      const tokens = getTokens()
      if (tokens !== null) {
        callback(tokens)
      } else {
        if (loadingView) setLoadingView(false)
        void procAtSignout().then(() => {
          commonModalStore.openAlertModal({ body: '許可されていない操作です。(tokens)' })
          if (router.currentRoute.value.path !== '/') void router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
        })
      }
    }
    // 処理中表示
    if (loadingView) setLoadingView(true)
    const miiaFwStore = useMiiaFwStore()
    const { refreshState, isAuthorized } = storeToRefs(miiaFwStore)
    if (isAuthorized.value) {
      invokeCallback()
    } else {
      const unwatch = watch(isAuthorized, newVal => {
        if (newVal === true) {
          invokeCallback()
        } else {
          if (loadingView) setLoadingView(false)
          void procAtSignout().then(() => {
            commonModalStore.openAlertModal({ body: '許可されていない操作です。(Signout)' })
            if (router.currentRoute.value.path !== '/') {
              void router.push({ path: '/' })
            } // 同じページに遷移させるとNavigation Errorになる。
          })
        }
        unwatch() // 監視を解除
        clearTimeout(timerObj)
      })
      // 5秒以内にトークンを取得できなかった場合はタイムアウト
      const timerObj = setTimeout(() => {
        unwatch()
        if (refreshState.value?.onRefresh) {
          // トークンリフレッシュ中は再帰的に自分を実行する。
          procUnderAuthed(callback, loadingView)
        } else {
          void procAtSignout().then(() => {
            commonModalStore.openAlertModal({ body: '許可されていない操作です。(unwatch)' })
            if (loadingView) setLoadingView(false)
            if (router.currentRoute.value.path !== '/') {
              void router.push({ path: '/' })
            } // 同じページに遷移させるとNavigation Errorになる。
          })
        }
      }, 5000)
    }
  }

  /**
   * 再認証およびトークンをリフレッシュする。
   * @param tokens getTokensで取得できるセッション情報
   * @returns If resolved then {'access_token': (string)アクセストークン, 'refresh_token': (string)リフレッシュトークン, 'id_token': (string)IDトークン, 'device_key': (string)デバイスキー, 'auth_token': Authトークン, 'token_secret': (string)トークンシークレット, 'provider': (string)IDプロバイダ}. セッション情報を返す。
   * If rejected then {'message': '[エラーメッセージ]'}.
   */
  const recertification = (tokens: TokenInfo): Promise<TokenInfo | string | ErrorMessage> => {
    const recertificationDialogStore = useRecertificationDialogStore()
    return new Promise(async (resolve, reject) => {
      if (
        Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
        tokens.provider.toLowerCase() === 'cognito'
      ) {
        let password = ''
        setLoadingView(false)
        try {
          password = await recertificationDialogStore.openDialog()
        } catch (error) {
          reject({ message: error })
          return
        }
        try {
          const userId = getUserIdToStorage()
          const res = await signin(userId, password)
          if ('error' in res) {
            reject(res.error)
            return
          }
          // signinのみだとAWS Credentialが署名付きリクエストをしてくれなくなり、
          // 後続のrefreshTokenProcをするとリクエストされるようになる。
          tokens = res.tokens
        } catch {
          try {
            const result = recertification(tokens)
            resolve(result)
            return
          } catch (error) {
            reject({ message: (error as { message: string }).message })
            return
          }
        }
        try {
          const result = refreshTokenProc(tokens)
          resolve(result)
          return
        } catch (error) {
          reject({ message: (error as { message: string }).message })
          return
        }
      }
    })
  }

  return {
    refreshToken,
    getTokens,
    refreshTokenProc,
    setTokens,
    procUnderAuthed
  }
}
